<template>
  <Modal :open="open" @close="close">
    <div>
      <h1 class="text-center font-extrabold text-xl">Modifier les informations du contrat</h1>
      <div v-if="planSaleCompleteUpdateAllowed">
        <p class="font-bold text-xl pb-2 tracking-tight text-gray-900">Contrat</p>
        <div class="flex-1 mt-1">
          <label for="planSaleDate" class="flex items-center text-sm text-gray-700">Date de début du contrat</label>
          <input
            type="date"
            id="planSaleDate"
            class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
            placeholder="01/01/1970"
            v-model="localPlanSale.date"
          />
        </div>
      </div>
      <div class="mt-4">
        <p class="font-bold text-xl pb-2 tracking-tight text-gray-900">
          {{ $t("Customer") }}
        </p>
        <div class="flex-1 mt-1">
          <label for="planSaleCustomerEmail" class="flex items-center text-sm text-gray-700">Email</label>
          <input
            type="email"
            id="planSaleCustomerEmail"
            class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
            v-model="localPlanSale.customer.email"
          />
        </div>
        <div v-if="planSaleCompleteUpdateAllowed" class="flex-1 mt-1">
          <label for="planSaleCustomerInformationLastName" class="flex items-center text-sm text-gray-700">Nom</label>
          <input
            type="text"
            id="planSaleCustomerInformationLastName"
            class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
            v-model="localPlanSale.customerInformation.lastName"
          />
        </div>
        <div v-if="planSaleCompleteUpdateAllowed" class="flex-1 mt-2">
          <label for="planSaleCustomerInformationFirstName" class="flex items-center text-sm text-gray-700">
            Prénom
          </label>
          <input
            type="text"
            id="planSaleCustomerInformationFirstName"
            class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
            v-model="localPlanSale.customerInformation.firstName"
          />
        </div>
        <div v-if="planSaleCompleteUpdateAllowed" class="flex-1 mt-2">
          <label for="planSaleCustomerInformationPhone" class="flex items-center text-sm text-gray-700">
            Numéro de téléphone
          </label>
          <input
            type="tel"
            id="planSaleCustomerInformationPhone"
            class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
            v-model="localPlanSale.customerInformation.phone"
          />
        </div>
        <div v-if="planSaleCompleteUpdateAllowed" class="flex-1 mt-2">
          <GoogleAddress class="flex-1" :initialAddress="customerAddress" @newAddress="updateCustomerAddress" />
        </div>
      </div>
      <div class="mt-4" v-if="localPlanSale.planSaleProduct">
        <p class="font-bold text-xl pb-2 tracking-tight text-gray-900">
          {{ $t("Products") }}
        </p>
        <div v-for="product in localPlanSale.planSaleProduct" :key="product.id" class="mt-2">
          <div class="flex-1 mt-1">
            <label :for="'planSaleProductTitle-' + product.id" class="flex items-center text-sm text-gray-700">
              Title
            </label>
            <input
              type="text"
              :id="'planSaleProductTitle-' + product.id"
              class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              v-model="product.title"
            />
          </div>
          <div class="flex-1 mt-2">
            <label :for="'planSaleProductReferenceId-' + product.id" class="flex items-center text-sm text-gray-700">
              Numéro de série
            </label>
            <input
              type="text"
              :id="'planSaleProductReferenceId-' + product.id"
              class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              v-model="product.referenceId"
            />
          </div>
          <div class="flex-1 mt-2">
            <label :for="'planSaleProductPrice' + product.id" class="flex items-center text-sm text-gray-700">
              Prix
            </label>
            <input
              type="number"
              :id="'planSaleProductPrice' + product.id"
              class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              aria-describedby="price-currency"
              v-model="product.price"
            />
          </div>
        </div>
      </div>
      <div class="mt-6 flex flex-col items-center space-y-4 text-center">
        <p class="text-sm text-gray-700 max-w-md">
          Si vous modifiez une information qui figure sur le bulletin d'adhésion, un nouveau bulletin est généré et
          envoyé au client assuré.
        </p>
        <GenericButton @click="updatePlanSale">Modifier le contrat</GenericButton>
      </div>
    </div>
  </Modal>
</template>

<script>
import { Modal, GenericButton } from "@estaly/ui";
import GoogleAddress from "@/components/address/GoogleAddress";

export default {
  components: {
    Modal,
    GenericButton,
    GoogleAddress,
  },

  props: ["open", "planSale", "planSaleCompleteUpdateAllowed"],

  data() {
    return {
      localPlanSale: {},
    };
  },

  mounted() {
    this.localPlanSale = {
      date: this.convertDate(this.planSale.date),
      customer: {
        email: this.planSale.customer.email,
      },
      customerInformation: {
        firstName: this.planSale.customer.firstName,
        lastName: this.planSale.customer.lastName,
        phone: this.planSale.customer.phone,
        address1: this.planSale.customer.address.address1,
        city: this.planSale.customer.address.city,
        province: this.planSale.customer.address.province,
        country: this.planSale.customer.address.country,
        zipCode: this.planSale.customer.address.zipCode,
      },
      planSaleProduct: this.planSale.product?.map((product) => ({
        id: product.id,
        title: product.title,
        referenceId: product.referenceId,
        price: product.price,
      })),
    };
  },

  computed: {
    customerAddress() {
      const address = this.planSale.customer.address;
      return `${address.address1}, ${address.city}, ${address.province}, ${address.country}`;
    },
  },

  methods: {
    updatePlanSale() {
      this.$emit("updatePlanSale", this.localPlanSale);
    },

    close() {
      this.$emit("closeUpdateModal");
    },

    updateCustomerAddress(newAddress) {
      this.localPlanSale.customerInformation = {
        ...this.localPlanSale.customerInformation,
        address1: newAddress.address1,
        city: newAddress.city,
        province: newAddress.province,
        country: newAddress.country,
        zipCode: newAddress.zipCode,
      };
    },

    convertDate(dateStr) {
      // Convert date from format "dd/mm/yyyy" to format "yyyy-MM-dd"
      if (dateStr) {
        const parts = dateStr.split("/");
        if (parts.length === 3) {
          return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
      }
      return null;
    },
  },
};
</script>
